import axios from "axios";
import qs from "qs"; // npm install qs

export async function fetchArticleBySlug(slug) {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;
  try {
    if (!slug || !serverUrl) {
      throw new Error("Missing slug or server URL");
    }

    const query = qs.stringify(
      {
        filters: {
          slug: { $eq: slug },
        },
        populate: {
          cover: { populate: "*" },
          category: { populate: "*" },
          demo_courses: {
            populate: {
              course_image: { populate: "*" },
              pointer: {
                populate: {
                  pointer_icon: { populate: "*" }
                }
              }
            }
          },
          subcategories: { populate: "*" },
          BlogFaq: { populate: "*" },
          author: { populate: "*" },
          SEO: { populate: "*" },
          // For dynamic zones in Strapi v5, you must use "on" and list each component
          blocks: {
            on: {
              "shared.rich-text": {
                populate: "*",
              },
              "shared.quote": {
                populate: "*",
              },
              "shared.media": {
                populate: {
                  file: { populate: "*" },
                },
              },
              "shared.table": {
                populate: {
                  Column: {
                    // matches exactly what Strapi returns ("Column")
                    populate: {
                      Row: {
                        // matches the repeatable component inside Column
                        populate: "*",
                      },
                    },
                  },
                },
              },
              "shared.cta": {
                populate: "*",
              },
              "shared.editor": {
                populate: "*",
              }
            },
          },
        },
      },
      { encodeValuesOnly: true },
    );

    const apiUrl = `${serverUrl}/articles?${query}`;
    const response = await axios.get(apiUrl);
    const fetchedArticle = response.data?.data?.[0];
    return fetchedArticle || null;
  } catch (error) {
    console.error("Error fetching article by slug:", error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to fetch article: HTTP status ${error.response.status}`,
      );
    } else {
      throw new Error(`Failed to fetch article: ${error.message}`);
    }
  }
}

// rest of your functions stay exactly the same...
export async function fetchAllArticles() {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;
  try {
    if (!serverUrl) {
      throw new Error("Server URL is not defined");
    }
    const response = await axios.get(
      `${serverUrl}/articles?sort=createdAt:desc&pagination[limit]=11&populate=*`,
    );

    if (!response.data?.data) {
      throw new Error("Invalid response format from API");
    }
    return response.data.data;
  } catch (error) {
    console.error("Error fetching all articles:", error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to fetch articles: HTTP status ${error.response.status}`,
      );
    } else {
      throw new Error(`Failed to fetch articles: ${error.message}`);
    }
  }
}

export async function fetchSubcategories(categoryId) {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;
  try {
    if (!serverUrl) {
      throw new Error("Server URL is not defined");
    }
    const response = await axios.get(
      `${serverUrl}/subcategories?filters[category][id][$eq]=${categoryId}&populate=*`,
    );
    if (!response.data?.data) {
      throw new Error("Invalid response format from API");
    }
    return response.data.data;
  } catch (error) {
    console.error("Error fetching all articles:", error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to fetch articles: HTTP status ${error.response.status}`,
      );
    } else {
      throw new Error(`Failed to fetch articles: ${error.message}`);
    }
  }
}

export async function fetchRecentBlogsByCategory(categoryId, delayMs = 500) {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;
  try {
    if (!serverUrl) {
      throw new Error("Server URL is not defined");
    }

    // Wait for page to load before fetching recent blogs
    await new Promise(resolve => setTimeout(resolve, delayMs));

    const response = await axios.get(
      `${serverUrl}/articles?filters[category][id][$eq]=${categoryId}&sort=createdAt:desc&populate=*&pagination[limit]=11`,
    );

    if (!response.data?.data) {
      throw new Error("Invalid response format from API");
    }
    return response.data.data;
  } catch (error) {
    console.error("Error fetching recent blogs by category:", error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to fetch blogs: HTTP status ${error.response.status}`,
      );
    } else {
      throw new Error(`Failed to fetch blogs: ${error.message}`);
    }
  }
}

export async function updateArticleViewCount(documentId, newViewCount) {
  // ⚠️ Change: Point the URL to your Next.js API route
  const url = `/api/increment-view-count`;

  // 🔑 SECURE: We no longer include the sensitive Authorization header here
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  // Send only the data the proxy needs
  const raw = JSON.stringify({
    documentId: documentId,
    newViewCount: newViewCount,
  });

  const requestOptions = {
    method: "POST", // Method must match the Next.js route handler
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  try {
    const response = await fetch(url, requestOptions);
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Update Failed:", error);
    throw error;
  }
}


export async function fetchRelatedArticles(
  subcategoryDocumentIds,
  currentArticleDocumentId,
  delayMs = 500
) {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;

  try {
    if (!serverUrl) {
      throw new Error("Server URL is not defined");
    }
    const query = qs.stringify(
      {
        filters: {
          subcategories: {
            documentId: {
              $eq: subcategoryDocumentIds,
            },
          },
        },
        populate: "*",
      },
      { encodeValuesOnly: true },
    );

    const apiUrl = `${serverUrl}/articles?${query}`;
    const response = await axios.get(apiUrl);
    const articles = response.data?.data || [];

    const relatedArticles = (articles || [])
      .filter((article) => {
        const articleDocumentId =
          article?.documentId || article?.attributes?.documentId || null;

        // Exclude current article
        if (articleDocumentId === currentArticleDocumentId) {
          return false;
        }

        return true;
      })
      .slice(0, 6);


    return relatedArticles;
  } catch (error) {
    console.error("Error fetching related articles:", error);

    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to fetch related articles: HTTP status ${error.response.status}`
      );
    } else {
      throw new Error(
        `Failed to fetch related articles: ${error.message}`
      );
    }
  }
}